import { z } from "zod";

import { boxCategorySchema } from "./boxCategories.js";

/**
 * Storefront chat (boxed chat v2, scoped to one environment). A room is identified by its id
 * within the caller's environment; the environment is never on the wire, it is the caller's.
 * The site rooms are the members here (adding one is a member: nothing stored is keyed on the
 * list); each live break also has its own room, `live-break-{liveBreakId}`, which exists while
 * the live break does.
 */
export const CHAT_ROOM_IDS = ["general"] as const;
export type ChatRoomId = (typeof CHAT_ROOM_IDS)[number];

/** A site room: one of `CHAT_ROOM_IDS`. */
export function isChatRoomId(value: string): value is ChatRoomId {
  return (CHAT_ROOM_IDS as readonly string[]).includes(value);
}

/** What a live break's room id starts with; the live break's id follows. */
export const LIVE_BREAK_CHAT_ROOM_PREFIX = "live-break-";

const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/** The chat room of one live break: `live-break-{liveBreakId}`. */
export function liveBreakChatRoomId(liveBreakId: string): string {
  return `${LIVE_BREAK_CHAT_ROOM_PREFIX}${liveBreakId}`;
}

/**
 * The live break a room id names, by its form alone (`live-break-` and a ULID), or null for any
 * other id. Whether that live break exists in the caller's environment is the server's check.
 */
export function parseLiveBreakChatRoomId(roomId: string): string | null {
  if (!roomId.startsWith(LIVE_BREAK_CHAT_ROOM_PREFIX)) return null;
  const liveBreakId = roomId.slice(LIVE_BREAK_CHAT_ROOM_PREFIX.length);
  return ULID_PATTERN.test(liveBreakId) ? liveBreakId : null;
}

/** A room id of a form chat knows: a site room or a live break's. Shape only, no lookup. */
export function isChatRoomIdForm(roomId: string): boolean {
  return isChatRoomId(roomId) || parseLiveBreakChatRoomId(roomId) !== null;
}

/**
 * The emotes a shopper reacts to a message with or sends floating over a room: boxed's channel
 * emotes (`boxed-frontend/lib/channel/emojiCatalog.ts`), in its tray order, as plain ids. The art
 * is the storefront's: boxed's image URLs never reach the wire.
 */
export const CHAT_EMOTES = [
  { id: "laugh", label: "Laugh" },
  { id: "love", label: "Love" },
  { id: "cool", label: "Cool" },
  { id: "gold", label: "Gold" },
  { id: "rage", label: "Rage" },
  { id: "cry", label: "Cry" },
  { id: "dead", label: "Dead" },
  { id: "gasp", label: "Gasp" },
  { id: "scared", label: "Scared" },
  { id: "pray", label: "Pray" },
  { id: "spicy", label: "Spicy" },
  { id: "thumbs_up", label: "Thumbs up" },
  { id: "cowboy", label: "Cowboy" },
  { id: "jail", label: "Jail" },
  { id: "pepe", label: "Pepe" },
] as const;
export type ChatEmoteId = (typeof CHAT_EMOTES)[number]["id"];
export const CHAT_EMOTE_IDS = CHAT_EMOTES.map((emote) => emote.id) as [
  ChatEmoteId,
  ...ChatEmoteId[],
];
export const chatEmoteIdSchema = z.enum(CHAT_EMOTE_IDS);

/** An emote id `CHAT_EMOTES` holds; the guard for anything read back from storage. */
export function isChatEmoteId(value: string): value is ChatEmoteId {
  return (CHAT_EMOTE_IDS as readonly string[]).includes(value);
}

/** Who wrote a message: a shopper, or the platform (a system row, `system` set). */
export const CHAT_SENDER_TYPES = ["USER", "SYSTEM"] as const;
export type ChatSenderType = (typeof CHAT_SENDER_TYPES)[number];

/** Longest message, in UTF-16 code units after the strip and trim (boxed's `MAX_CHAT_MESSAGE_LENGTH`). */
export const MAX_CHAT_MESSAGE_LENGTH = 200;
/** How many messages a room's history returns (boxed's `CHAT_HISTORY_LIMIT`). */
export const CHAT_HISTORY_LIMIT = 120;
/** Longest report description and mute reason. */
export const MAX_CHAT_NOTE_LENGTH = 200;

export const CHAT_REPORT_CATEGORIES = ["HARASSING", "INAPPROPRIATE", "SPAM", "SUSPICIOUS"] as const;
export type ChatReportCategory = (typeof CHAT_REPORT_CATEGORIES)[number];

/** Why a message was refused before it was stored: `details.reason` on `chat_message_rejected`. */
export const CHAT_REJECT_REASONS = ["empty", "too_long", "profanity", "link"] as const;
export type ChatRejectReason = (typeof CHAT_REJECT_REASONS)[number];

/** The quoted parent of a reply, snapshotted at send. `deleted` once the parent is removed (text dropped). */
export const chatReplyToSchema = z.object({
  messageId: z.string(),
  endUserId: z.string(),
  displayName: z.string(),
  text: z.string().optional(),
  deleted: z.boolean().optional(),
});
export type ChatReplyTo = z.infer<typeof chatReplyToSchema>;

/** A shopper as a system row names them: their username, else `Player XXXX` (`endUserDisplayName`). */
const chatSystemPersonSchema = z.object({ displayName: z.string() });

/** The box a live-break system row is about, at the live-break price one box sold for, in cents. */
const chatSystemBoxSchema = z.object({
  name: z.string(),
  priceCents: z.int(),
  imageUrl: z.string(),
});

/**
 * What a system row says, by `kind`; the storefront words it (a system row stores no text, so
 * the store's terms and currency reach every row). Written only by the platform:
 * - `live_break_purchase` / `live_break_cancel`, in the live break's room: who queued, or left the
 *   queue with, `amount` of which box;
 * - `live_break_big_hit`, in the live break's room once every viewer has seen the box: an epic or
 *   legendary pull (`rarity` 3 or 4, `getItemRarity` against the entry's live-break price);
 * - `live_break_announce`, in the site room `general`: the host's live break, its first ten boxes
 *   and its categories, and `path`, the storefront path that opens it.
 */
export const chatSystemPayloadSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("live_break_purchase"),
    liveBreakId: z.string(),
    user: chatSystemPersonSchema,
    box: chatSystemBoxSchema,
    amount: z.int(),
  }),
  z.object({
    kind: z.literal("live_break_cancel"),
    liveBreakId: z.string(),
    user: chatSystemPersonSchema,
    box: chatSystemBoxSchema,
    amount: z.int(),
  }),
  z.object({
    kind: z.literal("live_break_big_hit"),
    liveBreakId: z.string(),
    user: chatSystemPersonSchema,
    box: chatSystemBoxSchema,
    item: z.object({
      name: z.string(),
      imageUrl: z.string(),
      valueCents: z.int(),
      rarity: z.int(),
    }),
  }),
  z.object({
    kind: z.literal("live_break_announce"),
    liveBreakId: z.string(),
    title: z.string(),
    host: chatSystemPersonSchema,
    boxes: z.array(z.object({ name: z.string(), imageUrl: z.string() })),
    categories: z.array(boxCategorySchema),
    /** The storefront path that opens the live break, `/live-breaks/{liveBreakId}`. */
    path: z.string(),
  }),
]);
export type ChatSystemPayload = z.infer<typeof chatSystemPayloadSchema>;
export type ChatSystemKind = ChatSystemPayload["kind"];

/** Most boxes an announcement names (boxed's `ANNOUNCED_BOX_LIMIT`). */
export const LIVE_BREAK_ANNOUNCE_BOX_LIMIT = 10;

/** The storefront path a live break opens at; a system row links to it. */
export function liveBreakStorefrontPath(liveBreakId: string): string {
  return `/live-breaks/${liveBreakId}`;
}

/**
 * One emote's reactions on a message: how many shoppers reacted with it (the authoritative total,
 * not a delta) and whether the viewer is one of them (false without a session).
 */
export const chatReactionSchema = z.object({
  emote: chatEmoteIdSchema,
  count: z.int(),
  reacted: z.boolean(),
});
export type ChatReaction = z.infer<typeof chatReactionSchema>;

/**
 * A chat message as shoppers see it. Built from an allowlist (`toChatMessage`), never the stored
 * item: who deleted it and the tenant/environment ids never leave the server. A deleted message
 * keeps its place in history with `deleted: true` and no text. A system row (`senderType`
 * `SYSTEM`) has `system` and no text, and its `endUserId` and `displayName` are empty.
 */
export const chatMessageSchema = z.object({
  messageId: z.string(),
  roomId: z.string(),
  endUserId: z.string(),
  /** The sender's username when the message was sent. */
  displayName: z.string(),
  text: z.string().optional(),
  replyTo: chatReplyToSchema.optional(),
  deleted: z.boolean(),
  /** ISO 8601. */
  createdAt: z.string(),
  /** `SYSTEM` on the platform's system row; absent on a shopper's message (`USER`). */
  senderType: z.enum(CHAT_SENDER_TYPES).optional(),
  /** What a system row says; only on `SYSTEM`. */
  system: chatSystemPayloadSchema.optional(),
  /** The message's reactions in first-use order, on history reads; absent when it has none. */
  reactions: z.array(chatReactionSchema).optional(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

/** GET /v1/chat/rooms/{roomId}/messages. `viewer` only with a session. */
export const chatHistoryResponseSchema = z.object({
  roomId: z.string(),
  messages: z.array(chatMessageSchema),
  /** Shoppers who sent a heartbeat in the last two minutes. */
  headcount: z.int(),
  viewer: z
    .object({
      muted: z.boolean(),
      /** ISO 8601; absent when not muted or muted indefinitely. */
      mutedUntil: z.string().optional(),
    })
    .optional(),
});
export type ChatHistoryResponse = z.infer<typeof chatHistoryResponseSchema>;

export const sendChatMessageBodySchema = z.object({
  text: z.string().max(2000),
  replyToMessageId: z.string().max(64).optional(),
});
export type SendChatMessageBody = z.infer<typeof sendChatMessageBodySchema>;

export const sendChatMessageResponseSchema = z.object({ message: chatMessageSchema });

export const reportChatMessageBodySchema = z.object({
  category: z.enum(CHAT_REPORT_CATEGORIES),
  description: z.string().trim().max(MAX_CHAT_NOTE_LENGTH).optional(),
});
export type ReportChatMessageBody = z.infer<typeof reportChatMessageBodySchema>;

export const reportChatMessageResponseSchema = z.object({ reportId: z.string() });

export const chatHeartbeatResponseSchema = z.object({ headcount: z.int() });

/** POST /v1/chat/rooms/{roomId}/messages/{messageId}/reactions: the caller's reaction, set or cleared. */
export const setChatReactionBodySchema = z.object({
  emote: chatEmoteIdSchema,
  /** True adds the caller's reaction with this emote, false removes it; a repeat changes nothing. */
  reacted: z.boolean(),
});
export type SetChatReactionBody = z.infer<typeof setChatReactionBodySchema>;

export const setChatReactionResponseSchema = z.object({
  messageId: z.string(),
  emote: chatEmoteIdSchema,
  reacted: z.boolean(),
  /** How many shoppers react with this emote now. */
  count: z.int(),
});
export type SetChatReactionResponse = z.infer<typeof setChatReactionResponseSchema>;

/** POST /v1/chat/rooms/{roomId}/emotes: an emote floated over the room, shown live and never stored. */
export const sendChatEmoteBodySchema = z.object({ emote: chatEmoteIdSchema });
export type SendChatEmoteBody = z.infer<typeof sendChatEmoteBodySchema>;

/** The realtime namespace; one channel per room: `/chat/{environmentId}/{roomId}`. */
export const CHAT_CHANNEL_NAMESPACE = "chat";
export function chatChannel(environmentId: string, roomId: string): string {
  return `/${CHAT_CHANNEL_NAMESPACE}/${environmentId}/${roomId}`;
}

/** What the platform publishes on a room's channel. */
export const chatEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("message"), message: chatMessageSchema, headcount: z.int() }),
  z.object({ type: z.literal("message-deleted"), roomId: z.string(), messageId: z.string() }),
  z.object({ type: z.literal("presence"), roomId: z.string(), headcount: z.int() }),
  /** One shopper's reaction set or cleared; `count` is the emote's total on the message now. */
  z.object({
    type: z.literal("reaction"),
    roomId: z.string(),
    messageId: z.string(),
    emote: chatEmoteIdSchema,
    endUserId: z.string(),
    reacted: z.boolean(),
    count: z.int(),
  }),
  /** An emote floated over the room by a shopper, under their username. */
  z.object({
    type: z.literal("emote"),
    roomId: z.string(),
    emote: chatEmoteIdSchema,
    endUserId: z.string(),
    displayName: z.string(),
    /** ISO 8601. */
    createdAt: z.string(),
  }),
]);
export type ChatEvent = z.infer<typeof chatEventSchema>;

/** Checks an incoming realtime payload; null for anything this client does not understand. */
export function parseChatEvent(payload: unknown): ChatEvent | null {
  const parsed = chatEventSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}
